import './styles/base.css';
import './styles/layout.css';
import { route, notFound, navigate, initRouter } from './router.js';
import { initCookieBanner } from './components/cookie-banner.js';
import { renderFooter } from './components/footer.js';
import { initBottomNav } from './components/BottomNav.js';
import { renderImpressum } from './pages/impressum.js';
import { renderDatenschutz } from './pages/datenschutz.js';
import { renderAgb } from './pages/agb.js';
import { renderLogin } from './pages/login.js';
import { renderRegister } from './pages/register.js';
import { renderWaitlist } from './pages/waitlist.js';
import { renderForgotPassword } from './pages/forgot-password.js';
import { renderResetPassword } from './pages/reset-password.js';
import { renderEmailVerifizierung } from './pages/email-verifizierung.js';
import { renderAdmin } from './pages/admin.js';
import { renderHome } from './pages/home.js';
import { renderDashboard } from './pages/dashboard.js';
import { renderJailPage } from './pages/jail.js';
import { renderSettings } from './pages/settings/settings.js';
import { renderShop } from './pages/shop/shop.js';
import { renderStreamerPage } from './pages/community/streamer-page.js';
import { renderPublicStream } from './pages/community/stream-public.js';
import { renderStreamerDashboard } from './pages/dashboard/streamer-dashboard.js';
import { initSession } from './services/auth.js';
import { initNpcMessageCenter } from './services/npc-messages.js';
import { initGlobalToast } from './services/global-toast.js';
import { checkLaundryDebtOnLogin } from './components/LaundryDebtBanner.js';
import { initScrollToTop } from './components/ScrollToTop.js';
import { initPwaInstallHint } from './components/pwa-install-hint.js';
import { initActivityPing } from './services/activity-ping.service.js';
import { initPrestigeTheme } from './utils/prestige-theme.js';

// ─── Routen registrieren ──────────────────────────────────────────────────────

route('/', renderHome, false);
route('/home', renderHome, false);

route('/login', renderLogin, false);
route('/register', renderRegister, false);
// NT-ALPHA-REWORK-01: Alte Bewerbungs-Route deaktiviert – leitet auf die Self-Service-Registrierung um.
route('/alpha', () => { navigate('/register'); }, false);
// Alpha-Warteliste (nur für eingeloggte Warteliste-Spieler; Guard leitet dorthin um)
route('/warteliste', renderWaitlist, true);
route('/forgot-password', renderForgotPassword, false);
route('/reset-password', renderResetPassword, false);
route('/email-verifizierung', renderEmailVerifizierung, false);
route('/admin', renderAdmin, true);

route('/dashboard', renderDashboard, true);
route('/settings', renderSettings, true);
// NT-LAYOUT-01 (L5): Kladde und Brett laufen im Dashboard-Content-Bereich (Kopfleiste,
// Seitenleiste und Bottom-Nav bleiben sichtbar) statt als eigenständige Seiten.
route('/kladde', renderDashboard, true);
route('/brett', renderDashboard, true);
// NT-SHOP-01: Shop nur für eingeloggte Alpha-Spieler (mit Chris abgestimmt, Schritt 6).
route('/shop', renderShop, true);
// Streamer-Dashboard: eigene Twitch-Auth, kein Spieler-Login nötig
route('/streamer-dashboard', () => void renderStreamerDashboard(), false);
route('/profile', renderDashboard, true);
route('/jail', renderJailPage, true);

// ─── Karriere-Routen ──────────────────────────────────────────────────────────
route('/jobs',       renderDashboard, true);
route('/talentbaum', renderDashboard, true);
// NT-LAYOUT-01 (L5): Jobs-Unteransichten mit eigener URL, damit Browser-Zurück im Dashboard bleibt
route('/jobs/pfand-betrieb',    renderDashboard, true);
route('/jobs/werkstatt',        renderDashboard, true);
route('/jobs/werkstatt/craft',  renderDashboard, true);

// ─── Bottom-Nav-Ziele (NT-LAYOUT-01, L2) ──────────────────────────────────────
// Crime-Ops-Tab mit vorausgewählter Sektion (siehe utils/dashboard-routes.ts)
route('/labor',        renderDashboard, true);
route('/gewaechshaus', renderDashboard, true);
route('/rolltisch',    renderDashboard, true);
route('/viertel',      renderDashboard, true);

// ─── Business-Routen ──────────────────────────────────────────────────────────
route('/business/crime-ops',  renderDashboard, true);
route('/business/lagerhaus',  renderDashboard, true);
route('/business/markt',      renderDashboard, true);
route('/business/boerse',     renderDashboard, true);
route('/business', () => { navigate('/business/crime-ops'); }, true);

// ─── Immobilien-Routen ────────────────────────────────────────────────────────
route('/immobilien/geschaefte', renderDashboard, true);
route('/immobilien/lager',      renderDashboard, true);
route('/immobilien/verstecke',  renderDashboard, true);
route('/immobilien', () => { navigate('/immobilien/geschaefte'); }, true);

// ─── Community-Routen ─────────────────────────────────────────────────────────
route('/community/leaderboard', renderDashboard, true);
route('/community/stream',      renderDashboard, true, renderPublicStream);
route('/community/marktplatz',  renderDashboard, true);
route('/community/:slug',       () => void renderStreamerPage(), true);
route('/community', () => { navigate('/community/leaderboard'); }, true);

// ─── Organisations-Route (NT-ORG-01) ──────────────────────────────────────────
route('/organisation', renderDashboard, true);

// ─── Rechtliche Pflichtseiten ─────────────────────────────────────────────────

route('/impressum', renderImpressum, false);
route('/datenschutz', renderDatenschutz, false);
route('/agb', renderAgb, false);

notFound(() => {
  const app = document.getElementById('app');
  if (app) app.innerHTML = '<p style="padding:2rem;color:var(--accent-red)">404 – SEITE NICHT GEFUNDEN</p>';
});

// ─── Viewport-Height CSS-Variable (Mobile Browser-Chrome Fix) ────────────────
// Behebt: 100vh schliesst Browser-Adressleiste nicht immer korrekt ein
// In CSS: height: calc(var(--vh, 1vh) * 100) statt height: 100vh
function setViewportHeight(): void {
  document.documentElement.style.setProperty(
    '--vh',
    `${(window.innerHeight * 0.01).toFixed(2)}px`
  );
}
window.addEventListener('resize', setViewportHeight);

// ─── Modal-Viewport-Height (Bildschirmtastatur-Fix) ──────────────────────────
// Bug: Auf iOS/Android schrumpft die sichtbare Fläche beim Fokussieren eines
// Inputs (Bildschirmtastatur), aber body.modal-open (position:fixed) verhindert
// das automatische Scrollen des fokussierten Inputs ins Sichtfeld — Buttons am
// unteren Modal-Rand (z.B. VERSENDEN) werden dadurch von der Tastatur verdeckt
// und sind nicht mehr erreichbar. --modal-vh trackt die tatsächlich sichtbare
// Höhe (visualViewport statt innerHeight), .modal nutzt es als max-height-Cap.
function updateModalViewportHeight(): void {
  const vh = window.visualViewport?.height ?? window.innerHeight;
  document.documentElement.style.setProperty('--modal-vh', `${String(vh)}px`);
}
updateModalViewportHeight();
window.visualViewport?.addEventListener('resize', updateModalViewportHeight);
window.visualViewport?.addEventListener('scroll', updateModalViewportHeight);

// ─── Modal-Scroll-Lock (iOS Safari) ──────────────────────────────────────────
// iOS ignoriert touch-action: none und overscroll-behavior auf Backdrop-Elementen.
// Fix: body mit position:fixed sperren wenn ein Modal offen ist.
let _modalScrollY = 0;

function lockBodyScroll(): void {
  _modalScrollY = window.scrollY;
  document.body.style.top = `-${String(_modalScrollY)}px`;
  document.body.classList.add('modal-open');
}

function unlockBodyScroll(): void {
  document.body.classList.remove('modal-open');
  document.body.style.top = '';
  window.scrollTo(0, _modalScrollY);
}

const _modalObserver = new MutationObserver(() => {
  const open = document.querySelector('.modal-backdrop') !== null;
  const locked = document.body.classList.contains('modal-open');
  if (open && !locked) lockBodyScroll();
  else if (!open && locked) unlockBodyScroll();
});
_modalObserver.observe(document.body, { childList: true, subtree: true });

// ─── App initialisieren ───────────────────────────────────────────────────────

async function initApp(): Promise<void> {
  // Viewport-Height CSS-Variable initialisieren
  setViewportHeight();

  // Footer auf allen Seiten einmalig rendern
  renderFooter();

  // Bottom-Navigation für Mobile einmalig erstellen
  initBottomNav();

  // Cookie-Banner anzeigen wenn noch keine Entscheidung vorliegt (TTDSG § 25)
  initCookieBanner();

  // PWA-Install-/Homescreen-Onboarding (NT-PUSH-PWA-01) — Voraussetzung für Push (3/5)
  initPwaInstallHint();

  // Aktivitäts-Ping (NT-OFFLINE-CAP-01) — meldet echte Nutzer-Interaktion für den Offline-Cap
  initActivityPing();
  initPrestigeTheme();

  // Globaler Scroll-to-Top Button (alle Seiten, inkl. Legal-Seiten)
  initScrollToTop();

  // Bestehende Session wiederherstellen (CSRF + Spielerprofil) falls nt_csrf Cookie vorhanden
  await initSession();

  // Globales NPC-Erstkontakt-Modal (blockierend) beim Login/Online prüfen.
  initNpcMessageCenter();

  // Globaler Benachrichtigungs-Toast (NT-TOAST-GLOBAL-01) — lebt in der App-Shell, erscheint in
  // jedem Tab, nicht nur im Crime-Ops-Auftraggeber-Tab (Vorgänger dort entfernt).
  initGlobalToast();

  // Router starten (liest aktuellen Hash und rendert entsprechende Seite)
  initRouter();

  // Laundry-Schulden-Check: Banner einmal pro Session wenn Schulden vorhanden
  // Kurze Verzögerung damit die Seite zuerst rendert (Banner überlagert dann korrekt)
  setTimeout(() => { void checkLaundryDebtOnLogin(); }, 400);
}

void initApp();

// Service Worker registrieren (auch im Dev-Server — sonst hängt jeder Subscribe-Versuch in
// subscribeToPush() auf `navigator.serviceWorker.ready` endlos, da dieses Promise ohne jemals
// registrierten SW nie auflöst). sw.js' Offline-Caching nutzt Network-first für Navigation, stört
// den Vite-Dev-Server also nicht.
// Versionierung/Cache-Invalidierung läuft über SW_VERSION in public/sw.js (siehe pwa.constants.ts).
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/sw.js').catch(() => {
    // SW-Registrierungsfehler still ignorieren
  });
}
